import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Plus, Trash2, HandCoins, FileText, Download, FileSpreadsheet } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";

import { ChangeNote } from "@/components/ChangeNote";
import { fmtDate } from "@/lib/dates";

const CATEGORIES = ["Tithe", "Offering", "Building Fund", "Missions", "Youth"];
const METHODS = ["Cash", "Online", "Check"];
const money = (n) => `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 0 })}`;

const CAT_TINT = {
  Tithe: "#2D5A27", Offering: "#D97706", "Building Fund": "#0284C7", Missions: "#9A3412", Youth: "#84A98C",
};

const FY_MONTHS = ["Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec", "Jan", "Feb", "Mar"];
const fyOf = (dateStr) => {
  const [y, m] = String(dateStr).split("-").map(Number);
  return m >= 4 ? y : y - 1;
};
const fyLabel = (y) => `FY ${y}–${String(y + 1).slice(-2)}`;
const CURRENT_FY = fyOf(new Date().toISOString().slice(0, 10));

const EMPTY = { donor_name: "", category: "Tithe", amount: "", method: "Cash", date: new Date().toISOString().slice(0, 10), receipt_status: "Pending" };

export default function Donations() {
  const [donations, setDonations] = useState([]);
  const [members, setMembers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [catFilter, setCatFilter] = useState("all");
  const [stmtOpen, setStmtOpen] = useState(false);
  const [year, setYear] = useState(new Date().getFullYear());
  const [summary, setSummary] = useState(null);

  const load = () => {
    api.get("/donations", { params: { include_finance: true } }).then((r) => setDonations(r.data)).catch(() => {});
    api.get("/members").then((r) => setMembers(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const [fy, setFy] = useState(CURRENT_FY);
  const fyOptions = useMemo(() => {
    const s = new Set([CURRENT_FY, ...donations.map((d) => fyOf(d.date))]);
    return [...s].sort((a, b) => b - a);
  }, [donations]);

  const fyDonations = useMemo(() => donations.filter((d) => fyOf(d.date) === fy), [donations, fy]);

  const totals = useMemo(() => {
    const t = {};
    let total = 0;
    const monthly = FY_MONTHS.map((m) => ({ month: m, total: 0, prev: 0 }));
    donations.forEach((d) => {
      if (fyOf(d.date) === fy - 1) monthly[(Number(String(d.date).slice(5, 7)) + 8) % 12].prev += d.amount;
    });
    fyDonations.forEach((d) => {
      t[d.category] = (t[d.category] || 0) + d.amount;
      total += d.amount;
      const m = Number(String(d.date).slice(5, 7));
      monthly[(m + 8) % 12].total += d.amount;
    });
    const today = new Date().toISOString().slice(0, 10);
    const cutoff = fy === CURRENT_FY ? `${Number(today.slice(0, 4)) - 1}${today.slice(4)}` : `${fy}-03-31`;
    const prevTotal = donations
      .filter((d) => fyOf(d.date) === fy - 1 && String(d.date) <= cutoff)
      .reduce((s, d) => s + d.amount, 0);
    const changePct = prevTotal ? Math.round(((total - prevTotal) / prevTotal) * 1000) / 10 : null;
    return { byCat: t, total, monthly, changePct };
  }, [fyDonations, donations, fy]);

  const filtered = catFilter === "all" ? fyDonations : fyDonations.filter((d) => d.category === catFilter);

  const save = async () => {
    if (!form.donor_name || !form.amount) { toast.error("Donor and amount are required"); return; }
    try {
      await api.post("/donations", { ...form, amount: parseFloat(form.amount) });
      toast.success("Contribution recorded");
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch { toast.error("Could not record contribution"); }
  };

  const remove = async (id) => {
    try { await api.delete(`/donations/${id}`); toast.success("Removed"); load(); }
    catch { toast.error("Could not remove"); }
  };

  const openStatements = async (y = year) => {
    setStmtOpen(true);
    try {
      const { data } = await api.get(`/giving/summary?year=${y}`);
      setSummary(data);
    } catch { toast.error("Could not load statements"); }
  };

  const changeYear = async (y) => {
    setYear(Number(y));
    try {
      const { data } = await api.get(`/giving/summary?year=${y}`);
      setSummary(data);
    } catch { toast.error("Could not load statements"); }
  };

  const downloadStatement = async (donor) => {
    try {
      const res = await api.get(`/giving/statement?donor=${encodeURIComponent(donor)}&year=${year}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `giving-statement-${donor.replace(/ /g, "-")}-${year}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Statement downloaded");
    } catch { toast.error("Could not generate statement"); }
  };

  const exportDonations = async (fmt) => {
    try {
      const qs = new URLSearchParams({ format: fmt, fy: String(fy) });
      if (catFilter !== "all") qs.set("category", catFilter);
      const res = await api.get(`/donations/export?${qs.toString()}`, { responseType: "blob" });
      const type = fmt === "pdf" ? "application/pdf" : "text/csv";
      const url = window.URL.createObjectURL(new Blob([res.data], { type }));
      const a = document.createElement("a");
      a.href = url; a.download = `donations-report-FY${fy}-${fy + 1}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success(`Exported ${fmt.toUpperCase()}`);
    } catch { toast.error("Export failed"); }
  };

  return (
    <div className="space-y-6" data-testid="donations-page">
      <div className="flex items-center gap-3">
        <Label className="shrink-0 text-[#526050]">Fiscal year</Label>
        <Select value={String(fy)} onValueChange={(v) => setFy(Number(v))}>
          <SelectTrigger className="w-40 h-11 bg-white" data-testid="donations-fy-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {fyOptions.map((y) => (
              <SelectItem key={y} value={String(y)} data-testid={`fy-option-${y}`}>{fyLabel(y)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-xs text-[#7A8A78]" data-testid="donations-fy-range">1 Apr {fy} – 31 Mar {fy + 1}</span>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-5 bg-[#2D5A27] text-white border-0 shadow-sm col-span-2" data-testid="donations-year-total">
          <p className="text-xs font-semibold uppercase tracking-widest text-white/70">Year Total Income · {fyLabel(fy)}</p>
          <p className="mt-2 font-serif text-3xl font-bold">{money(totals.total)}</p>
          <ChangeNote pct={totals.changePct} dark testid="donations-year-change" />
        </Card>
        {[["Tithe", "Tithes"], ["Offering", "Offerings"]].map(([c, label]) => (
          <Card key={c} className="p-5 bg-white border border-[#E5E0D8] shadow-sm" data-testid={`donations-total-${c.toLowerCase()}`}>
            <div className="h-2 w-8 rounded-full mb-2" style={{ backgroundColor: CAT_TINT[c] }} />
            <p className="text-xs font-medium text-[#7A8A78]">{label}</p>
            <p className="mt-1 font-serif text-xl font-bold text-[#1C241B]">{money(totals.byCat[c] || 0)}</p>
          </Card>
        ))}
      </div>

      <Card className="p-5 bg-white border border-[#E5E0D8] shadow-sm" data-testid="donations-monthly-chart">
        <p className="font-serif text-lg font-bold text-[#1C241B] mb-3">Monthly Totals · {fyLabel(fy)} vs {fyLabel(fy - 1)}</p>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={totals.monthly} margin={{ left: 0, right: 10, top: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E0D8" />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#7A8A78" }} />
              <YAxis tick={{ fontSize: 12, fill: "#7A8A78" }} tickFormatter={(v) => `$${v.toLocaleString()}`} width={70} />
              <Tooltip formatter={(v) => money(v)} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="linear" dataKey="prev" name={fyLabel(fy - 1)} stroke="#A8B5A6" strokeWidth={2} strokeDasharray="6 4" dot={{ r: 3, fill: "#A8B5A6" }} />
              <Line type="linear" dataKey="total" name={fyLabel(fy)} stroke="#2D5A27" strokeWidth={2.5} dot={{ r: 4, fill: "#D97706" }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <Select value={catFilter} onValueChange={setCatFilter}>
          <SelectTrigger className="w-44 h-11 bg-white" data-testid="donations-category-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex flex-wrap gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid="donations-export-button" className="h-11 border-[#2D5A27] text-[#2D5A27] hover:bg-[#EAF2E8]">
                <Download className="h-4 w-4 mr-2" /> Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-white">
              <DropdownMenuItem data-testid="donations-export-csv" onClick={() => exportDonations("csv")}>
                <FileSpreadsheet className="h-4 w-4 mr-2 text-[#2D5A27]" /> Download CSV
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="donations-export-pdf" onClick={() => exportDonations("pdf")}>
                <FileText className="h-4 w-4 mr-2 text-[#9A3412]" /> Download PDF
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="outline" data-testid="giving-statements-button" onClick={() => openStatements()}
            className="h-11 border-[#2D5A27] text-[#2D5A27] hover:bg-[#EAF2E8]">
            <FileText className="h-4 w-4 mr-2" /> Giving Statements
          </Button>
          <Button data-testid="log-donation-button" onClick={() => { setForm(EMPTY); setOpen(true); }}
            className="h-11 bg-[#D97706] hover:bg-[#B45309] text-white">
            <Plus className="h-4 w-4 mr-2" /> Log Contribution
          </Button>
        </div>
      </div>

      <Card className="bg-white border border-[#E5E0D8] shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent border-[#E5E0D8]">
              <TableHead>Date</TableHead>
              <TableHead>Donor</TableHead>
              <TableHead>Category</TableHead>
              <TableHead className="hidden sm:table-cell">Method</TableHead>
              <TableHead className="hidden sm:table-cell">Receipt</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((d) => (
              <TableRow key={d.id} className="border-[#E5E0D8]" data-testid={`donation-row-${d.id}`}>
                <TableCell className="text-sm text-[#526050]">{fmtDate(d.date)}</TableCell>
                <TableCell className="font-medium text-[#1C241B]">{d.donor_name}</TableCell>
                <TableCell>
                  <Badge variant="outline" className="font-normal border-[#E5E0D8]"
                    style={{ color: CAT_TINT[d.category], borderColor: `${CAT_TINT[d.category]}40` }}>
                    {d.category}
                  </Badge>
                </TableCell>
                <TableCell className="hidden sm:table-cell text-sm text-[#526050]">
                  {d.from_finance ? (
                    <span className="text-[10px] uppercase tracking-wider text-[#0369A1] bg-[#E0F2FE] rounded px-1.5 py-0.5" data-testid={`from-finance-tag-${d.id}`}>From Finances</span>
                  ) : d.method}
                </TableCell>
                <TableCell className="hidden sm:table-cell">
                  <span className={`text-xs font-medium ${d.receipt_status === "Sent" ? "text-[#2D5A27]" : "text-[#B45309]"}`}>
                    {d.receipt_status}
                  </span>
                </TableCell>
                <TableCell className="text-right font-semibold text-[#1C241B]">{money(d.amount)}</TableCell>
                <TableCell className="text-right">
                  {d.from_finance ? (
                    <span className="sm:hidden text-[10px] uppercase tracking-wider text-[#0369A1]">Finances</span>
                  ) : (
                  <Button size="icon" variant="ghost" onClick={() => remove(d.id)} data-testid={`delete-donation-${d.id}`} className="h-8 w-8 text-destructive">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center py-12 text-[#7A8A78]">No contributions in {fyLabel(fy)}.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-md" data-testid="donation-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl flex items-center gap-2">
              <HandCoins className="h-5 w-5 text-[#D97706]" /> Log Contribution
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Donor name</Label>
              <Input list="donor-list" data-testid="donation-donor-input" value={form.donor_name}
                onChange={(e) => setForm({ ...form, donor_name: e.target.value })} placeholder="Start typing a name..." />
              <datalist id="donor-list">
                {members.map((m) => <option key={m.id} value={`${m.first_name} ${m.last_name}`} />)}
              </datalist>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Amount</Label>
                <Input type="number" data-testid="donation-amount-input" value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="0.00" />
              </div>
              <div className="space-y-1.5">
                <Label>Date</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="donation-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Method</Label>
                <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{METHODS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-donation-button" className="bg-[#D97706] hover:bg-[#B45309] text-white">Record</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={stmtOpen} onOpenChange={setStmtOpen}>
        <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="statements-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl flex items-center gap-2">
              <FileText className="h-5 w-5 text-[#2D5A27]" /> Annual Giving Statements
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="flex items-center gap-3">
              <Label className="shrink-0">Tax year</Label>
              <Select value={String(year)} onValueChange={changeYear}>
                <SelectTrigger className="w-32" data-testid="statement-year-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(summary?.available_years || [year]).map((y) => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="divide-y divide-[#E5E0D8] border border-[#E5E0D8] rounded-lg">
              {(summary?.donors || []).map((d) => (
                <div key={d.donor_name} className="flex items-center justify-between px-4 py-3" data-testid={`statement-donor-${d.donor_name.replace(/ /g, '-')}`}>
                  <div>
                    <p className="font-medium text-[#1C241B]">{d.donor_name}</p>
                    <p className="text-xs text-[#7A8A78]">{d.count} contributions · ${d.total.toLocaleString()}</p>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => downloadStatement(d.donor_name)}
                    data-testid={`download-statement-${d.donor_name.replace(/ /g, '-')}`}
                    className="border-[#2D5A27] text-[#2D5A27] hover:bg-[#EAF2E8]">
                    <Download className="h-4 w-4 mr-1" /> PDF
                  </Button>
                </div>
              ))}
              {summary && summary.donors.length === 0 && (
                <p className="px-4 py-8 text-center text-sm text-[#7A8A78]">No contributions recorded for {year}.</p>
              )}
              {!summary && <p className="px-4 py-8 text-center text-sm text-[#7A8A78]">Loading…</p>}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
